import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronUp, Loader2, Lock, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import { formatErrorDetail } from '../utils/formatError';

const PATHWAY_CONTENT = {
  A: {
    name: 'Founders Track',
    headline: 'You are ready to build.',
    body: 'Your assessment shows strong foundations across the dimensions that matter. You have the mindset, the resilience and enough financial runway to take this seriously. The Founders Track is a guided build from idea through to a working business.',
    includes: [
      'Stages 2–9: The full guided programme',
      'Brian, your AI guide, with you at every step',
      'Templates, frameworks and real tools for each stage',
      'Your Springboard workspace from Stage 4',
      'Real human mentor access when you need it',
    ],
    stages: 'Stages 2–9',
    packageId: 'pathway_a',
    colour: '#16a34a',
    colourLight: '#dcfce7',
  },
  B: {
    name: 'Employment Track',
    headline: 'Your strengths point to the right role.',
    body: 'Your assessment shows real capability, but your strongest path forward right now is into the right employed role rather than starting a business from scratch. That is not a consolation — it is the honest recommendation. This pathway helps you find that role, using what you already have.',
    includes: [
      'Stage 2: Employment direction and strengths mapping',
      'CV rebuild and personal brand positioning',
      'Job search strategy and tracking tools',
      'Human mentor access for guidance and accountability',
    ],
    stages: 'Stage 2',
    packageId: 'pathway_b',
    colour: '#2563eb',
    colourLight: '#dbeafe',
  },
  C: {
    name: 'Mindset Discovery',
    headline: 'Start with the foundations.',
    body: 'Your assessment found areas that need building before a full business journey makes sense. That is useful information, not a judgement. The Mindset Discovery pathway gives you the structure and support to build confidence, clarity and direction at your own pace.',
    includes: [
      'Stages 2–4: Foundation building at your pace',
      'Self-assessment and service definition',
      'Mindset, resilience and confidence tools',
      'Human mentor access for support',
    ],
    stages: 'Stages 2–4',
    packageId: 'pathway_c',
    colour: '#9333ea',
    colourLight: '#f3e8ff',
  },
};

const LIGHT_COLOURS = {
  green: { label: 'Green Light', colour: '#16a34a' },
  yellow: { label: 'Yellow Light', colour: '#ca8a04' },
  orange: { label: 'Orange Light', colour: '#ea580c' },
  red: { label: 'Red Light', colour: '#dc2626' },
};

const DIMENSION_NAMES = {
  mindset: 'Mindset & Motivation',
  financial: 'Financial Readiness',
  life: 'Life Circumstances',
  resilience: 'Resilience',
  idea_fit: 'Idea & Market Fit',
};

const Results = () => {
  const navigate = useNavigate();
  const { token, user, refreshUser } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDim, setExpandedDim] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/assessment/stage1/results`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setResults(res.data.results);
      } catch {
        navigate('/stage1-assessment');
      }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#999' }} />
      </div>
    );
  }

  if (!results) return null;

  const pathway = results.pathway || 'A';
  const content = PATHWAY_CONTENT[pathway] || PATHWAY_CONTENT.A;
  const light = LIGHT_COLOURS[results.light_colour] || LIGHT_COLOURS.yellow;
  const dims = results.dimensions || {};
  const stagesUnlocked = user?.stages_unlocked;

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/create-checkout`, {
        package_id: content.packageId,
        origin_url: origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || 'Checkout unavailable');
      setCheckoutLoading(false);
    }
  };

  const handleDemoBypass = async () => {
    try {
      await axios.post(`${API}/payments/demo-bypass`, {
        package_id: content.packageId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demo: Pathway unlocked');
      await refreshUser();
      navigate('/portal/stages');
    } catch { toast.error('Failed'); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafafa', fontFamily: 'Arial, sans-serif' }} data-testid="results-page">
      {/* Header bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e5e5', padding: '16px 24px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '18px', fontWeight: 800, color: '#1E491A', letterSpacing: '-0.5px' }}>SPRING.</span>
          <span style={{ fontSize: '13px', color: '#999' }}>Your Assessment Results</span>
        </div>
      </div>

      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '48px 24px 80px' }}>

        {/* Score summary — compact */}
        <div style={{
          background: '#fff', borderRadius: '20px', padding: '32px',
          border: '1px solid #e5e5e5', marginBottom: '24px', textAlign: 'center',
        }} data-testid="results-score-card">
          <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#999', marginBottom: '12px' }}>
            Your Readiness Score
          </p>
          <div style={{ fontSize: '56px', fontWeight: 800, color: '#1E491A', letterSpacing: '-2px', lineHeight: 1 }} data-testid="results-total-score">
            {results.total_score}<span style={{ fontSize: '22px', color: '#ccc', fontWeight: 400 }}> / 50</span>
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            marginTop: '16px', padding: '6px 16px', borderRadius: 0,
            background: `${light.colour}15`,
          }} data-testid="results-light-colour">
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: light.colour }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: light.colour }}>{light.label}</span>
          </div>
        </div>

        {/* Pathway recommendation — the main event */}
        <div style={{
          background: '#fff', borderRadius: '20px', padding: '40px 32px',
          border: `2px solid ${content.colour}33`, marginBottom: '24px',
        }} data-testid="results-pathway-card">
          <div style={{
            display: 'inline-block', padding: '6px 16px', borderRadius: 0,
            background: content.colourLight, marginBottom: '20px',
          }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: content.colour, letterSpacing: '0.5px' }}>
              PATHWAY {pathway}: {content.name.toUpperCase()}
            </span>
          </div>

          <h1 style={{ fontSize: '32px', fontWeight: 800, color: '#1E491A', letterSpacing: '-1px', marginBottom: '16px', lineHeight: 1.2 }} data-testid="results-pathway-headline">
            {content.headline}
          </h1>

          <p style={{ fontSize: '16px', color: '#444', lineHeight: 1.7, marginBottom: '28px' }} data-testid="results-pathway-body">
            {content.body}
          </p>

          {/* What's included */}
          <div style={{ background: '#fafafa', borderRadius: 0, padding: '24px', marginBottom: '28px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#999', marginBottom: '14px' }}>
              What's included — {content.stages}
            </p>
            {content.includes.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', fontSize: '14px', color: '#333', lineHeight: 1.5 }}>
                <Check style={{ width: 16, height: 16, color: content.colour, flexShrink: 0, marginTop: '3px' }} />
                {item}
              </div>
            ))}
          </div>

          {/* Unlock CTA */}
          {!stagesUnlocked ? (
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                data-testid="results-unlock-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  background: content.colour, color: '#fff', border: 'none',
                  padding: '18px 40px', borderRadius: 0,
                  fontSize: '17px', fontWeight: 700, cursor: checkoutLoading ? 'wait' : 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  marginBottom: '12px',
                }}
                onMouseEnter={(e) => { if (!checkoutLoading) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = `0 8px 24px ${content.colour}44`; } }}
                onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
              >
                {checkoutLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Lock style={{ width: 18, height: 18 }} />
                    Unlock {content.name} — $TBA
                  </>
                )}
              </button>
              <div>
                <button
                  onClick={handleDemoBypass}
                  data-testid="results-demo-bypass-btn"
                  style={{ background: 'none', border: 'none', color: '#999', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', marginTop: '4px' }}
                >
                  Skip Payment (Demo)
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => navigate('/portal/stages')}
                data-testid="results-go-stages-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  background: content.colour, color: '#fff', border: 'none',
                  padding: '18px 40px', borderRadius: 0,
                  fontSize: '17px', fontWeight: 700, cursor: 'pointer',
                }}
              >
                <Check style={{ width: 18, height: 18 }} />
                Go to Your Pathway
                <ArrowRight style={{ width: 18, height: 18 }} />
              </button>
            </div>
          )}
        </div>

        {/* Dimension breakdown — collapsible */}
        <div style={{
          background: '#fff', borderRadius: '20px', padding: '28px 32px',
          border: '1px solid #e5e5e5', marginBottom: '24px',
        }} data-testid="results-dimensions">
          <p style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#999', marginBottom: '18px' }}>
            Your Five Dimensions
          </p>
          {Object.entries(dims).map(([key, val]) => {
            const score = val?.score ?? 0;
            const maxScore = val?.max ?? 10;
            const pct = Math.round((score / maxScore) * 100);
            const isOpen = expandedDim === key;
            return (
              <div key={key} style={{ marginBottom: '12px' }} data-testid={`dim-${key}`}>
                <button
                  onClick={() => setExpandedDim(isOpen ? null : key)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                    background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{ flex: 1, fontSize: '14px', fontWeight: 600, color: '#1E491A' }}>
                    {DIMENSION_NAMES[key] || key}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#666', minWidth: '50px', textAlign: 'right' }}>
                    {score}/{maxScore}
                  </span>
                  {isOpen ? <ChevronUp style={{ width: 14, height: 14, color: '#999' }} /> : <ChevronDown style={{ width: 14, height: 14, color: '#999' }} />}
                </button>
                <div style={{ height: '4px', background: '#f0f0f0', borderRadius: '2px', overflow: 'hidden', margin: '4px 0 0' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: content.colour, borderRadius: '2px', transition: 'width 0.6s ease' }} />
                </div>
                {isOpen && val?.feedback && (
                  <p style={{ fontSize: '13px', color: '#666', lineHeight: 1.6, margin: '10px 0 4px', padding: '0 4px' }}>
                    {val.feedback}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* View badge link */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigate('/badge')}
            data-testid="results-view-badge-btn"
            style={{ background: 'none', border: 'none', color: '#999', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
          >
            View Your Completion Badge
          </button>
        </div>
      </div>
    </div>
  );
};

export default Results;
